import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { client } from './AuthContext';

const emptyOffice = () => ({
  city: '',
  address: '',
  phone: '',
  email: '',
  map_query: '',
});

const empty = {
  hero_title: '',
  hero_subtitle: '',
  hotline: '',
  hotline_hours: '',
  whatsapp_sales: '',
  email_support: '',
  offices: [],
};

export default function AdminContact() {
  const [form, setForm] = useState(empty);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    client
      .get('/api/site/contact')
      .then((r) => setForm({ ...empty, ...r.data }))
      .finally(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await client.put('/api/admin/contact', form);
      toast.success('Halaman Kontak tersimpan');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Gagal menyimpan');
    } finally {
      setSaving(false);
    }
  };

  const setOffice = (i, k, v) => {
    const arr = [...form.offices];
    arr[i] = { ...arr[i], [k]: v };
    setForm({ ...form, offices: arr });
  };
  const addOffice = () =>
    setForm({ ...form, offices: [...form.offices, emptyOffice()] });
  const rmOffice = (i) =>
    setForm({ ...form, offices: form.offices.filter((_, idx) => idx !== i) });

  return (
    <div data-testid="admin-contact-page">
      <p className="text-xs tracking-[0.25em] text-px-red font-semibold mb-3">
        HALAMAN KONTAK
      </p>
      <h1 className="font-display text-4xl font-bold text-neutral-900 mb-2">
        Halaman Kontak
      </h1>
      <p className="text-neutral-600 mb-8">
        Alamat kantor, hotline, WhatsApp, dan email dukungan.
      </p>

      <form
        onSubmit={save}
        className="bg-white rounded-2xl border border-neutral-200 p-8 space-y-6"
      >
        <Row label="Judul Hero">
          <input
            className={inputCls}
            value={form.hero_title}
            onChange={(e) => setForm({ ...form, hero_title: e.target.value })}
          />
        </Row>
        <Row label="Subtitle Hero">
          <textarea
            rows={2}
            className={`${inputCls} resize-none`}
            value={form.hero_subtitle}
            onChange={(e) =>
              setForm({ ...form, hero_subtitle: e.target.value })
            }
          />
        </Row>

        <div className="grid md:grid-cols-2 gap-5">
          <Row label="Hotline">
            <input
              className={inputCls}
              value={form.hotline}
              onChange={(e) => setForm({ ...form, hotline: e.target.value })}
            />
          </Row>
          <Row label="Jam Operasional Hotline">
            <input
              className={inputCls}
              value={form.hotline_hours}
              onChange={(e) =>
                setForm({ ...form, hotline_hours: e.target.value })
              }
            />
          </Row>
          <Row label="WhatsApp Sales">
            <input
              className={inputCls}
              value={form.whatsapp_sales}
              onChange={(e) =>
                setForm({ ...form, whatsapp_sales: e.target.value })
              }
            />
          </Row>
          <Row label="Email Dukungan">
            <input
              type="email"
              className={inputCls}
              value={form.email_support}
              onChange={(e) =>
                setForm({ ...form, email_support: e.target.value })
              }
              data-testid="contact-email-support"
            />
          </Row>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs uppercase tracking-wider text-neutral-600 font-semibold">
              Kantor
            </label>
            <button
              type="button"
              onClick={addOffice}
              className="text-xs font-semibold text-px-red inline-flex items-center gap-1"
            >
              <Plus className="w-3.5 h-3.5" /> Tambah kantor
            </button>
          </div>
          <div className="space-y-4">
            {form.offices.map((o, i) => (
              <div
                key={i}
                className="bg-neutral-50 rounded-xl p-5 border border-neutral-200 grid grid-cols-1 md:grid-cols-2 gap-3 relative"
                data-testid={`office-editor-${i}`}
              >
                <button
                  type="button"
                  onClick={() => rmOffice(i)}
                  className="absolute top-3 right-3 text-neutral-400 hover:text-red-600"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
                <Row label="Nama Kantor / Kota">
                  <input
                    className={inputCls}
                    value={o.city}
                    onChange={(e) => setOffice(i, 'city', e.target.value)}
                  />
                </Row>
                <Row label="Telepon">
                  <input
                    className={inputCls}
                    value={o.phone}
                    onChange={(e) => setOffice(i, 'phone', e.target.value)}
                  />
                </Row>
                <div className="md:col-span-2">
                  <Row label="Alamat">
                    <textarea
                      rows={2}
                      className={`${inputCls} resize-none`}
                      value={o.address}
                      onChange={(e) =>
                        setOffice(i, 'address', e.target.value)
                      }
                    />
                  </Row>
                </div>
                <Row label="Email">
                  <input
                    className={inputCls}
                    value={o.email}
                    onChange={(e) => setOffice(i, 'email', e.target.value)}
                  />
                </Row>
                <Row label="Query Peta (Google Maps)">
                  <input
                    className={inputCls}
                    placeholder="mis. Menara+Jakarta+Fortune"
                    value={o.map_query}
                    onChange={(e) =>
                      setOffice(i, 'map_query', e.target.value)
                    }
                  />
                </Row>
              </div>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={saving || !loaded}
          data-testid="contact-save-btn"
          className="inline-flex items-center gap-2 bg-px-red hover:bg-red-700 disabled:opacity-60 text-white font-semibold px-7 py-3 rounded-full transition-colors"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Menyimpan...
            </>
          ) : (
            <>
              <Save className="w-4 h-4" /> Simpan Perubahan
            </>
          )}
        </button>
      </form>
    </div>
  );
}

const inputCls =
  'mt-2 w-full bg-white border border-neutral-200 focus:border-neutral-900 outline-none rounded-lg px-3 py-2.5 text-sm';

const Row = ({ label, children }) => (
  <div>
    <label className="text-[11px] uppercase tracking-wider text-neutral-600 font-semibold">
      {label}
    </label>
    {children}
  </div>
);
